import { apiClient, handleError } from '@/services/Axios';
import { isOnboardingScaffoldMode } from '@/config/onboarding';
import { mockRegisterCompany, mockAcceptInvite } from '@/services/onboardingScaffoldMocks';
import { unwrapV1, v1Post } from '@/services/v1Api';

async function ensureCsrfCookie() {
  await apiClient.get('/sanctum/csrf-cookie');
  await new Promise((resolve) => setTimeout(resolve, 100));
}

function authPayloadFromV1(response) {
  const { data, meta } = unwrapV1(response);
  return {
    ...(data && typeof data === 'object' ? data : {}),
    message: meta?.message,
  };
}

// ========== AUTH ============== //

export const login = async (email, password) => {
  try {
    await ensureCsrfCookie();

    const response = await apiClient.post('/api/v1/auth/login', {
      email,
      password,
    });
    const payload = authPayloadFromV1(response);

    if (!payload?.user) {
      throw new Error('Invalid response from server');
    }
    if (!payload.token) {
      throw new Error('Invalid response from server');
    }

    localStorage.setItem('auth_token', payload.token);

    const user = payload.user;
    localStorage.setItem(
      'user',
      JSON.stringify({
        ...user,
        username: user.username ?? user.email?.split('@')[0] ?? null,
      })
    );

    return payload;
  } catch (error) {
    console.error('Login Error:', error);
    handleError(error);
    throw error;
  }
};

/**
 * Create a new tenant company and first company admin user.
 * Response shape matches login: { user, token, company, address_book_company_id, ... }.
 */
export const registerCompany = async ({
  company_name,
  name,
  first_name,
  last_name,
  email,
  password,
  password_confirmation,
}) => {
  const resolvedName =
    (name && String(name).trim()) ||
    [first_name, last_name].filter(Boolean).join(' ').trim() ||
    '';

  try {
    if (isOnboardingScaffoldMode()) {
      return mockRegisterCompany({
        company_name,
        name: resolvedName || undefined,
        first_name,
        last_name,
        email,
      });
    }

    await ensureCsrfCookie();

    const response = await apiClient.post('/api/v1/auth/register-company', {
      company_name,
      name: resolvedName,
      email,
      password,
      password_confirmation,
    });
    const payload = authPayloadFromV1(response);

    if (!payload?.user) {
      throw new Error('Invalid response from server');
    }
    if (!payload?.token && !payload?.email_verification_required) {
      throw new Error('Invalid response from server');
    }

    return payload;
  } catch (error) {
    console.error('Register company error:', error);
    handleError(error);
    throw error;
  }
};

/**
 * Persist onboarding wizard answers for the signed-in user's tenant.
 *
 * @param {Record<string, unknown>} state Full session onboarding state (see onboardingWizard.js).
 */
export async function syncOnboardingWizardToServer(state) {
  try {
    if (isOnboardingScaffoldMode()) {
      return { success: true };
    }
    if (!state || typeof state !== 'object') {
      throw new Error('Invalid onboarding state');
    }

    await ensureCsrfCookie();

    const { data } = await v1Post('/api/v1/company/onboarding-wizard', {
      state: {
        version: state.version,
        account: state.account,
        company: state.company,
        financial: state.financial,
        locations: state.locations,
        team: state.team,
      },
    });

    return data ?? { success: true };
  } catch (error) {
    console.error('syncOnboardingWizardToServer:', error);
    handleError(error);
    throw error;
  }
}

/**
 * Request another verification email (guest).
 */
export const resendVerificationEmail = async (email) => {
  await ensureCsrfCookie();
  const { data, meta } = await v1Post('/api/v1/auth/resend-verification', {
    email: String(email).trim(),
  });
  return {
    ...(data && typeof data === 'object' ? data : {}),
    message: meta?.message,
    success: true,
  };
};

/**
 * Request a password reset email (guest).
 * Always returns a generic success message (no account enumeration).
 */
export const requestPasswordReset = async (email) => {
  await ensureCsrfCookie();
  const { data, meta } = await v1Post('/api/v1/auth/forgot-password', {
    email: String(email).trim(),
  });
  return {
    ...(data && typeof data === 'object' ? data : {}),
    message:
      meta?.message ||
      'If an account exists for this email, we sent a password reset link.',
    success: true,
  };
};

/**
 * Reset password with token from email link (guest).
 */
export const resetPasswordWithToken = async ({
  email,
  token,
  password,
  password_confirmation,
}) => {
  await ensureCsrfCookie();
  const { data, meta } = await v1Post('/api/v1/auth/reset-password', {
    email: String(email).trim(),
    token,
    password,
    password_confirmation,
  });
  return {
    ...(data && typeof data === 'object' ? data : {}),
    message: meta?.message,
    success: true,
  };
};

/**
 * Accept a team invite.
 */
export const acceptInvite = async ({ token, name, password, password_confirmation }) => {
  try {
    if (isOnboardingScaffoldMode()) {
      return mockAcceptInvite({ token, name, password, password_confirmation });
    }

    await ensureCsrfCookie();

    const response = await apiClient.post('/api/v1/user-invites/accept', {
      token,
      name,
      password,
      password_confirmation,
    });
    const payload = authPayloadFromV1(response);

    if (!payload?.user || !payload?.token) {
      throw new Error('Invalid response from server');
    }

    return payload;
  } catch (error) {
    console.error('Accept invite error:', error);
    handleError(error);
    throw error;
  }
};

export const logout = async () => {
  try {
    const token = localStorage.getItem('auth_token');

    if (!token) {
      return;
    }

    await apiClient.post('/api/v1/auth/logout');
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  } finally {
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
  }
};
